import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBarsFilter,
  faBookmark,
  faChevronDown,
  faInfoCircle,
  faThumbtack,
} from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import {
  FilterAvailableValues,
  JourneyFilterDefaultValueMode,
} from "../../types/analytics";
import AnalyticsFilterInputs, {
  AnalyticsFilterComparisonProperty,
  AnalyticsPageJourneyFilterRestriction,
} from "../../types/analytics/AnalyticsFilterInputs";
import { IDateRange } from "../../types/generic";
import { JourneyPicker } from "./filters";
import AnalyticsDatesControl, {
  AnalyticsDatesControlHandle,
} from "./filters/AnalyticsDatesControl";
import {
  AnalyticsDateMode,
  activityWindowWriteTarget,
  dateModeForShortcut,
  resolveDateChromeIntent,
} from "../../helpers/analytics/analyticsDateModeHelpers";
import AudienceFacetChipBar from "./filters/AudienceFacetChipBar";
import AnalyticsAdvancedFiltersPanel from "./filters/AnalyticsAdvancedFiltersPanel";
import { AlertPopup, Popover, Tooltip } from "../common";
import {
  analyticsFiltersAreDirty,
  isAudienceFacetDirty,
  isDateFilterDirty,
  isJourneyFilterDirty,
  isPointInTimeFilterDirty,
} from "../../helpers/analytics/analyticsFilterBarHelpers";
import {
  clampPointInTimeDate,
  isPointInTimeUsable,
  pointInTimeDateForDisplay,
  pageSupportsPointInTime,
  resolvePointInTimeAvailability,
  latestSelectablePointInTimeDate,
} from "../../helpers/analytics/pointInTimeFilterHelpers";
import AnalyticsPastDataInfoModal from "./filters/AnalyticsPastDataInfoModal";
import { useAnalyticsPointInTimeFloor } from "../../hooks/useAnalyticsPointInTimeFloor";
import {
  journeyFilterTitlesForKeys,
  savedJourneyLabelForDisplay,
} from "../../helpers/analytics/journeyFilterHelpers";
import { AnalyticsDateShortcut } from "../../types/analytics/AnalyticsSavedFilter";
import AnalyticsSaveFilterDialog from "./filters/AnalyticsSaveFilterDialog";
import AnalyticsRenameFilterDialog from "./filters/AnalyticsRenameFilterDialog";
import AnalyticsSavedFiltersMenu from "./filters/AnalyticsSavedFiltersMenu";
import { useAnalyticsSavedFilters } from "../../hooks/useAnalyticsSavedFilters";
import { useAnalyticsActivityWindowResolve } from "../../hooks/useAnalyticsActivityWindowResolve";
import AppRoutes from "../../routes/AppRoutes";

/**
 * Analytics filter bar: edits are staged until Apply (or discarded via Reset in the
 * amber strip). Charts keep using `appliedValues` until then. Halo classes mark only
 * the chips that differ from applied — not every chip when anything is dirty.
 */
interface AnalyticsFiltersProps {
  pageIdentifier: string;
  dynamicPageId?: number;
  pageTitle: string;
  journeyFilterDefaultValueMode: JourneyFilterDefaultValueMode;
  journeyFilterRestriction: AnalyticsPageJourneyFilterRestriction;
  availableValues: FilterAvailableValues;
  /** In-progress chip/panel selection (may differ from what charts use). */
  selectedValues: AnalyticsFilterInputs;
  /** Last applied selection — source of truth for chart queries. */
  appliedValues: AnalyticsFilterInputs;
  /** When set, the left label shows this saved filter's name instead of "Filters". */
  appliedSavedFilterId: number | null;
  onAppliedSavedFilterIdChange(id: number | null): void;
  onJourneyPickerChange: (selectedJourneyKeys: string[]) => void;
  onLocationPickerChange: (selectedLocationIds: number[]) => void;
  onJobtitlePickerChange: (selectedJobtitleIds: number[]) => void;
  onAppraisalLevelPickerChange: (selectedAppraisalLevelIds: number[]) => void;
  onComparePropertyChange: (
    newValue: AnalyticsFilterComparisonProperty,
  ) => void;
  onAllJourneysSelectedChange: (allSelected: boolean) => void;
  onChangeApprovedDataOnly: (newValue: boolean) => void;
  onChangeIncludeLeavers: (newValue: boolean) => void;
  onDateRangeChange: (newValue: IDateRange | null) => void;
  /**
   * Replace the full staged filter selection in one write (Advanced panel Apply).
   * Avoids stomping concurrent partial updates.
   */
  onStageFilterInputs: (values: AnalyticsFilterInputs) => void;
  /**
   * All-activity resolve / first-paint date window. May write staged only, or both
   * staged and applied when dates are not dirty (first paint / live FIT sync).
   */
  onActivityWindowDateRangeChange: (newValue: IDateRange | null) => void;
  onUserGroupPickerChange: (selectedUserGroupIds: number[]) => void;
  onApplyFilters(values?: AnalyticsFilterInputs): void;
  /** Restore page defaults (Advanced panel). Distinct from staged-strip Reset. */
  onResetFilters(): void;
  /** Revert staged selection to applied (staged-strip Reset). */
  onDiscardFilters(): void;
  onRecallFilters(
    values: AnalyticsFilterInputs,
    savedFilterId?: number | null,
  ): void;
  /**
   * Stage a Show past data date (ADR-0006). Null means live. Staged like any
   * other filter, so it only reaches the charts on Apply.
   */
  onPointInTimeDateChange(date: string | null): void;
  /**
   * Session-scoped opt-in to carry filters (and attached saved id) across analytics pages.
   * When off, each landing is page-local (pin or built-in).
   */
  carryFiltersAcrossPages: boolean;
  onCarryFiltersAcrossPagesChange(enabled: boolean): void;
  /**
   * When true, bootstrap may recall the page-default pin (after ?sf= handling).
   * False while pinned filters are restoring a session blob.
   */
  applyPageDefaultOnLoad?: boolean;
  /** Shared-filter id from `?sf=` — prefer over page default when set. */
  shareSavedFilterId?: number | null;
  /** Called after a successful or failed `?sf=` resolve attempt. */
  onShareSavedFilterResolved?(result: "applied" | "failed"): void;
  /**
   * Called once the initial saved-filter resolve finishes (share link, page default,
   * or confirmed nothing to apply). Parent should gate widget queries on this.
   * `generation` must match the parent's current bootstrap generation or the
   * completion is ignored (same idea as widget reloadGenerationRef).
   */
  onSavedFilterBootstrapComplete?(generation: number): void;
  /** Bumps when the parent needs saved-filter resolve to run again for this catalogue. */
  savedFilterBootstrapGeneration?: number;
}

function AnalyticsFilters({
  pageIdentifier,
  dynamicPageId,
  pageTitle,
  journeyFilterDefaultValueMode,
  journeyFilterRestriction,
  availableValues,
  selectedValues,
  appliedValues,
  appliedSavedFilterId,
  onAppliedSavedFilterIdChange,
  onJourneyPickerChange,
  onLocationPickerChange,
  onJobtitlePickerChange,
  onAppraisalLevelPickerChange,
  onComparePropertyChange,
  onDateRangeChange,
  onStageFilterInputs,
  onActivityWindowDateRangeChange,
  onAllJourneysSelectedChange,
  onChangeApprovedDataOnly,
  onChangeIncludeLeavers,
  onUserGroupPickerChange,
  onApplyFilters,
  onResetFilters,
  onDiscardFilters,
  onRecallFilters,
  onPointInTimeDateChange,
  carryFiltersAcrossPages,
  onCarryFiltersAcrossPagesChange,
  applyPageDefaultOnLoad = false,
  shareSavedFilterId = null,
  onShareSavedFilterResolved,
  onSavedFilterBootstrapComplete,
  savedFilterBootstrapGeneration = 0,
}: AnalyticsFiltersProps) {
  const { t } = useTranslation();

  // Skip the floor lookup entirely where point-in-time can never apply.
  const { floor: pointInTimeFloor, isLoading: pointInTimeFloorLoading } =
    useAnalyticsPointInTimeFloor(pageSupportsPointInTime(pageIdentifier));
  const pointInTimeAvailability = resolvePointInTimeAvailability({
    pageIdentifier,
    floor: pointInTimeFloor,
    floorIsLoading: pointInTimeFloorLoading,
  });
  const pointInTimeUsable = isPointInTimeUsable(pointInTimeAvailability);
  const stagedPointInTimeDate = selectedValues.pointInTimeDate?.trim() || null;
  const appliedPointInTimeDate = appliedValues.pointInTimeDate?.trim() || null;
  /** Chip follows the staged date (halo when it differs); the banner follows applied. */
  const showPointInTimeChip = pointInTimeUsable && stagedPointInTimeDate != null;
  const showPointInTimeBanner =
    pointInTimeUsable && appliedPointInTimeDate != null;

  const isDirty = analyticsFiltersAreDirty(selectedValues, appliedValues);
  const journeyDirty = isJourneyFilterDirty(selectedValues, appliedValues);
  const datesDirty = isDateFilterDirty(selectedValues, appliedValues);
  const pointInTimeDirty = isPointInTimeFilterDirty(
    selectedValues,
    appliedValues,
  );

  const [journeyOpen, setJourneyOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  const [pastDataInfoOpen, setPastDataInfoOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [audienceFacetPopoverOpen, setAudienceFacetPopoverOpen] =
    useState(false);
  /** Local journey checklist while the journey popover is open (null when closed). */
  const [journeyDraft, setJourneyDraft] = useState<{
    allJourneys: boolean;
    journeyKeys: string[];
  } | null>(null);
  const skipJourneyDraftDiscardRef = useRef(false);
  // dateMode = tab chrome; stagedDateShortcut = intent after Done (chip + FIT resolve).
  const [dateMode, setDateMode] = useState<AnalyticsDateMode>("fit");
  const [stagedDateShortcut, setStagedDateShortcut] =
    useState<AnalyticsDateShortcut>("FIT_TO_JOURNEYS");
  const [appliedDateShortcut, setAppliedDateShortcut] =
    useState<AnalyticsDateShortcut>("FIT_TO_JOURNEYS");
  /** Exact draft must have both dates before Done/Apply is allowed. */
  const [exactDatesComplete, setExactDatesComplete] = useState(true);
  /** Common draft must have a radio selected before Done/Apply is allowed. */
  const [commonDraftComplete, setCommonDraftComplete] = useState(true);
  /** Plays halo release/collapse before band Apply/Discard so CSS can finish. */
  const [bandApplyMotion, setBandApplyMotion] = useState<
    "apply" | "discard" | null
  >(null);
  const bandApplyTimerRef = useRef<number | null>(null);
  const datesControlRef = useRef<AnalyticsDatesControlHandle>(null);
  /** Next FIT resolve writes staged dates only (Done switched into All activity). */
  const nextFitResolveStagesOnlyRef = useRef(false);
  /** After Done, close without snapping mode chrome back to a stale shortcut. */
  const suppressDateModeSnapOnCloseRef = useRef(false);

  const availableJourneyKeys = useMemo(
    () =>
      availableValues.journeys.flatMap((g) => g.journeys.map((j) => j.key)),
    [availableValues.journeys],
  );

  const handleFitResolvedRange = (range: IDateRange | null) => {
    if (!range) return;
    const stagesOnlyRequested = nextFitResolveStagesOnlyRef.current;
    nextFitResolveStagesOnlyRef.current = false;
    const target = activityWindowWriteTarget({
      stagesOnlyRequested,
      journeyDirty,
      datesDirty,
      hasAppliedDateRange: !!appliedValues.dateRange?.startDate,
    });
    if (target === "staged-only") {
      onDateRangeChange(range);
    } else {
      onActivityWindowDateRangeChange(range);
    }
  };

  const { emptyActivityHint, activityWindowError, retryActivityWindow } =
    useAnalyticsActivityWindowResolve({
    stagedDateShortcut,
    allJourneys: selectedValues.allJourneys,
    journeyKeys: selectedValues.journeyKeys,
    availableJourneyKeys,
    onDateRangeChange: handleFitResolvedRange,
  });

  const snapDateModeToStagedShortcut = (
    shortcut: AnalyticsDateShortcut = stagedDateShortcut,
  ) => {
    setDateMode(dateModeForShortcut(shortcut));
  };

  const dateChromeDraftIncomplete =
    (dateMode === "pick" && !exactDatesComplete) ||
    (dateMode === "common" && !commonDraftComplete);

  const chipPopoverOpen =
    journeyOpen || datesOpen || pastOpen || audienceFacetPopoverOpen;

  const sameJourneyDraft = (
    draft: { allJourneys: boolean; journeyKeys: string[] },
  ) => {
    if (draft.allJourneys !== selectedValues.allJourneys) return false;
    const a = [...(draft.journeyKeys ?? [])].map(String).sort();
    const b = [...(selectedValues.journeyKeys ?? [])].map(String).sort();
    return a.length === b.length && a.every((v, i) => v === b[i]);
  };

  const handleJourneyOpenChange = (open: boolean) => {
    if (open) {
      setJourneyDraft({
        allJourneys: selectedValues.allJourneys,
        journeyKeys: [...(selectedValues.journeyKeys ?? [])],
      });
      setJourneyOpen(true);
      return;
    }
    if (!skipJourneyDraftDiscardRef.current) {
      setJourneyDraft(null);
    }
    skipJourneyDraftDiscardRef.current = false;
    setJourneyOpen(false);
    setJourneyDraft(null);
  };

  const handleJourneyDone = () => {
    if (journeyDraft && !sameJourneyDraft(journeyDraft)) {
      if (journeyDraft.allJourneys) {
        onAllJourneysSelectedChange(true);
      } else {
        onJourneyPickerChange(journeyDraft.journeyKeys);
      }
    }
    skipJourneyDraftDiscardRef.current = true;
    setJourneyOpen(false);
    setJourneyDraft(null);
  };

  /** Read date chrome + drafts into a stageable result (no React writes). */
  const peekDateChromeIntent = () =>
    resolveDateChromeIntent({
      dateMode,
      stagedShortcut: stagedDateShortcut,
      stagedRange: selectedValues.dateRange,
      exactRange: datesControlRef.current?.getExactDateRange() ?? null,
      commonDraftPreset: datesControlRef.current?.getCommonDraftPreset() ?? null,
    });

  /**
   * Done on the dates popover: draft chrome → staged shortcut/range (not Apply).
   * Returns the staged shortcut, or null when Exact/Common draft is incomplete.
   */
  const stageDateIntentFromChrome = (): AnalyticsDateShortcut | null => {
    const resolved = peekDateChromeIntent();
    if (!resolved.ok) return null;
    if (resolved.changed) {
      if (resolved.fitResolveStagesOnly) {
        nextFitResolveStagesOnlyRef.current = true;
        setStagedDateShortcut("FIT_TO_JOURNEYS");
      } else if (resolved.range) {
        setStagedDateShortcut(resolved.shortcut);
        onDateRangeChange(resolved.range);
      } else {
        setStagedDateShortcut(resolved.shortcut);
      }
    }
    setDateMode(dateModeForShortcut(resolved.shortcut));
    return resolved.shortcut;
  };

  const handleDatesOpenChange = (open: boolean) => {
    if (!open) {
      if (suppressDateModeSnapOnCloseRef.current) {
        suppressDateModeSnapOnCloseRef.current = false;
      } else {
        snapDateModeToStagedShortcut();
      }
    }
    setDatesOpen(open);
  };

  /**
   * Advanced panel Apply: merge date chrome into the panel draft, stage once, Apply.
   * Returns false when date chrome is incomplete.
   * Passes `next` into Apply so charts do not use a stale selectedValues closure.
   */
  const applyPanelDraft = (draft: AnalyticsFilterInputs): boolean => {
    const resolved = peekDateChromeIntent();
    if (!resolved.ok) return false;
    setStagedDateShortcut(resolved.shortcut);
    setDateMode(dateModeForShortcut(resolved.shortcut));
    const next =
      resolved.range != null ? { ...draft, dateRange: resolved.range } : draft;
    onStageFilterInputs(next);
    applyStagedFilters(resolved.shortcut, next);
    suppressDateModeSnapOnCloseRef.current = true;
    return true;
  };

  /** Merge panel date chrome into a draft before Update / Save as new. */
  const preparePanelPersistDraft = (
    draft: AnalyticsFilterInputs,
  ): AnalyticsFilterInputs | null => {
    const resolved = peekDateChromeIntent();
    if (!resolved.ok) return null;
    if (resolved.shortcut !== stagedDateShortcut) {
      setStagedDateShortcut(resolved.shortcut);
      setDateMode(dateModeForShortcut(resolved.shortcut));
    }
    return resolved.range != null
      ? { ...draft, dateRange: resolved.range }
      : draft;
  };

  const handleDatesDone = () => {
    const staged = stageDateIntentFromChrome();
    if (staged == null) return;
    suppressDateModeSnapOnCloseRef.current = true;
    setDatesOpen(false);
  };

  const handlePanelClose = () => {
    if (suppressDateModeSnapOnCloseRef.current) {
      suppressDateModeSnapOnCloseRef.current = false;
    } else {
      snapDateModeToStagedShortcut();
    }
    setPanelOpen(false);
  };

  const handleStagedDateShortcutFromSaved = (
    shortcut: AnalyticsDateShortcut,
  ) => {
    setStagedDateShortcut(shortcut);
    setAppliedDateShortcut(shortcut);
    setDateMode(dateModeForShortcut(shortcut));
  };

  const saved = useAnalyticsSavedFilters({
    pageIdentifier,
    dynamicPageId,
    selectedValues,
    appliedValues,
    availableJourneyKeys,
    journeys: availableValues.journeys,
    isDirty,
    appliedSavedFilterId,
    onAppliedSavedFilterIdChange,
    onRecallFilters,
    onResetFilters: () => {
      setStagedDateShortcut("FIT_TO_JOURNEYS");
      setAppliedDateShortcut("FIT_TO_JOURNEYS");
      setDateMode("fit");
      onResetFilters();
    },
    carryFiltersAcrossPages,
    applyPageDefaultOnLoad,
    shareSavedFilterId,
    onShareSavedFilterResolved,
    onSavedFilterBootstrapComplete,
    savedFilterBootstrapGeneration,
    dateShortcut: stagedDateShortcut,
    onCommittedDateShortcutChange: handleStagedDateShortcutFromSaved,
  });

  useEffect(() => {
    return () => {
      if (bandApplyTimerRef.current != null) {
        window.clearTimeout(bandApplyTimerRef.current);
      }
    };
  }, []);

  /** Apply staged selection; drop saved-filter selection because the combo is no longer that save. */
  const applyStagedFilters = (
    shortcutAfterApply: AnalyticsDateShortcut = stagedDateShortcut,
    valuesOverride?: AnalyticsFilterInputs,
  ) => {
    if (appliedSavedFilterId != null) {
      onAppliedSavedFilterIdChange(null);
    }
    saved.clearPartialApplyState();
    setAppliedDateShortcut(shortcutAfterApply);
    onApplyFilters(valuesOverride);
  };

  const discardStagedFilters = () => {
    setStagedDateShortcut(appliedDateShortcut);
    setDateMode(dateModeForShortcut(appliedDateShortcut));
    onDiscardFilters();
  };

  const runBandApplyWithMotion = (kind: "apply" | "discard") => {
    if (bandApplyMotion != null) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Match CSS animation durations in analytics.css (skip delay when reduced motion).
    const delayMs = reduced ? 0 : kind === "apply" ? 280 : 180;
    setBandApplyMotion(kind);
    if (bandApplyTimerRef.current != null) {
      window.clearTimeout(bandApplyTimerRef.current);
    }
    bandApplyTimerRef.current = window.setTimeout(() => {
      bandApplyTimerRef.current = null;
      if (kind === "apply") {
        applyStagedFilters();
      } else {
        // Discard restores applied values; appliedSavedFilterId is unchanged so the name returns.
        discardStagedFilters();
      }
      setBandApplyMotion(null);
    }, delayMs);
  };

  const journeyChipLabel = useMemo(() => {
    if (selectedValues.allJourneys) {
      return t("Analytics.Filters.AllJourneys", "All journeys");
    }
    const keys = selectedValues.journeyKeys ?? [];
    if (keys.length === 0) {
      return t("Analytics.Filters.NoJourneys", "No journeys");
    }
    const titles = journeyFilterTitlesForKeys(
      availableValues.journeys,
      keys,
      t,
    );
    if (titles.length <= 2) return titles.join(", ");
    return `${titles[0]}, ${titles[1]} +${titles.length - 2}`;
  }, [selectedValues, availableValues, t]);

  const datesChipLabel = useMemo(() => {
    // Chip reads staged date intent only — mode chrome must not change this label.
    if (stagedDateShortcut === "FIT_TO_JOURNEYS") {
      return t("Analytics.Filters.AllActivity", "All activity");
    }
    if (
      !selectedValues.dateRange?.startDate ||
      !selectedValues.dateRange?.endDate
    ) {
      return t("Analytics.Filters.NoDates", "Not set");
    }
    return `${format(new Date(selectedValues.dateRange.startDate), "d MMM yyyy")} - ${format(
      new Date(selectedValues.dateRange.endDate),
      "d MMM yyyy",
    )}`;
  }, [selectedValues.dateRange, stagedDateShortcut, t]);

  const pointInTimeChipDateLabel = useMemo(() => {
    const display = pointInTimeDateForDisplay(stagedPointInTimeDate);
    return display ? format(display, "d MMM yyyy") : "";
  }, [stagedPointInTimeDate]);

  const pointInTimeBannerDateLabel = useMemo(() => {
    const display = pointInTimeDateForDisplay(appliedPointInTimeDate);
    return display ? format(display, "d MMMM yyyy") : "";
  }, [appliedPointInTimeDate]);

  /** Keep the picker inside the client's stored history before staging it. */
  const handlePointInTimeDateChange = (date: string) => {
    onPointInTimeDateChange(
      clampPointInTimeDate(
        date,
        pointInTimeFloor?.earliestSelectableDate,
        latestSelectablePointInTimeDate(),
      ),
    );
  };

  /** Absolute range text for the All-activity preview (chip shows "All activity"). */
  const fitPreviewRangeLabel = useMemo(() => {
    if (
      !selectedValues.dateRange?.startDate ||
      !selectedValues.dateRange?.endDate
    ) {
      return "-";
    }
    return `${format(new Date(selectedValues.dateRange.startDate), "d MMM yyyy")} - ${format(
      new Date(selectedValues.dateRange.endDate),
      "d MMM yyyy",
    )}`;
  }, [selectedValues.dateRange]);

  // Halo via CSS box-shadow only — do not swap border/bg to amber (that fought Apply branding).
  const chipClass = (dirty: boolean, quiet = false) =>
    cx(
      "inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm cursor-pointer",
      quiet
        ? "bg-white border-gray-200 font-medium text-gray-700"
        : "bg-gray-50 border-gray-300 font-semibold text-gray-800",
      dirty && "analytics-filter-chip--staged",
      dirty && bandApplyMotion === "apply" && "analytics-filter-chip--releasing",
      dirty &&
        bandApplyMotion === "discard" &&
        "analytics-filter-chip--collapsing",
    );

  return (
    <div className="mb-3">
      {/* Full-bleed chrome strip under #top-bar; square corners (not a card). -mx-2 cancels AnalyticsPage horizontal padding. */}
      <div
        id="analytics-filter-band"
        className="analytics-filter-band -mx-2 bg-gray-50 border-b border-gray-200"
      >
        <div className="flex items-start gap-2 px-3 py-2">
          {/* Own column so chips never wrap under the Filters / saved-filter label.
              __col makes it one chip tall so the label centres on the chip row
              instead of riding high against the top of it. */}
          <div className="analytics-filter-band__col flex flex-shrink-0 items-center self-start">
            <AnalyticsSavedFiltersMenu
              isOpen={savedOpen}
              onOpenChange={setSavedOpen}
              isDirty={isDirty}
              items={saved.savedItems}
              appliedSavedFilterId={appliedSavedFilterId}
              canPinPageDefault={!!saved.canPinPageDefault}
              onSaveCurrent={() => saved.openSaveDialog(false, selectedValues)}
              onRecall={saved.recall}
              onClearSavedSelection={saved.clearSavedSelection}
              onRename={saved.openRename}
              onShare={saved.share}
              onDelete={saved.handleDelete}
              onSetPageDefault={saved.setPageDefault}
              onClearPageDefault={saved.clearPageDefault}
              showPointInTimeTag={pointInTimeUsable}
              triggerElement={
                <button
                  id="analytics-filter-saved"
                  type="button"
                  className="inline-flex max-w-full items-center gap-1.5 px-1 py-1 text-sm font-semibold text-gray-600 hover:text-gray-900 rounded-md"
                  aria-label={
                    saved.appliedSavedFilterName ??
                    t("Analytics.Filters.FiltersLabel", "Filters")
                  }
                >
                  <FontAwesomeIcon icon={faBarsFilter} className="shrink-0" />
                  {saved.appliedSavedFilterName ? (
                    <TruncatedTextWithTooltip
                      text={saved.appliedSavedFilterName}
                      className="max-w-[140px] truncate"
                    />
                  ) : (
                    <span>
                      {t("Analytics.Filters.FiltersLabel", "Filters")}
                    </span>
                  )}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-[10px] opacity-50 shrink-0"
                  />
                </button>
              }
            />
          </div>

          {/* Journey, Dates, and audience chips wrap as siblings after the label column. */}
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {/* Journey chip */}
            <Popover
              openState={journeyOpen}
              onOpenStateChange={handleJourneyOpenChange}
              showCloseIcon={false}
              contentClassNames="w-80 bg-white p-3 border border-gray-200 shadow-lg-around"
              triggerElement={
                <button
                  id="analytics-filter-journey"
                  type="button"
                  className={chipClass(journeyDirty)}
                >
                  <span className="text-xs font-normal text-gray-500">
                    {t("Analytics.Filters.Journey", "Journey")}
                  </span>
                  <span className="max-w-[14rem] truncate">
                    {journeyChipLabel}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-[10px] opacity-50"
                  />
                </button>
              }
            >
              <div>
                <h5 className="text-sm font-bold mb-2">
                  {t("Analytics.Filters.Journeys", "Journeys")}
                </h5>
                {journeyDraft && (
                  <JourneyPicker
                    defaultValueMode={journeyFilterDefaultValueMode}
                    restriction={journeyFilterRestriction}
                    items={availableValues.journeys}
                    onValueChange={(keys) =>
                      setJourneyDraft({
                        allJourneys: false,
                        journeyKeys: keys,
                      })
                    }
                    allSelected={journeyDraft.allJourneys}
                    selectedValues={journeyDraft.journeyKeys}
                    inEditMode={true}
                    onBeginEditMode={() => {}}
                    onAllSelectedChange={(allSelected) => {
                      if (allSelected) {
                        const allKeys = availableJourneyKeys;
                        setJourneyDraft({
                          allJourneys: true,
                          journeyKeys: allKeys,
                        });
                      } else {
                        setJourneyDraft({
                          allJourneys: false,
                          journeyKeys: [],
                        });
                      }
                    }}
                  />
                )}
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    className="btn-primary !px-3 !py-1.5 text-sm rounded-md"
                    onClick={handleJourneyDone}
                  >
                    {t("Common.Done", "Done")}
                  </button>
                </div>
              </div>
            </Popover>

            {/* Dates chip */}
            <Popover
              openState={datesOpen}
              onOpenStateChange={handleDatesOpenChange}
              showCloseIcon={false}
              contentClassNames="w-80 bg-white p-3 border border-gray-200 shadow-lg-around"
              triggerElement={
                <button
                  id="analytics-filter-dates"
                  type="button"
                  className={chipClass(datesDirty)}
                >
                  <span className="text-xs font-normal text-gray-500">
                    {t("Analytics.Filters.Dates", "Dates")}
                  </span>
                  <span className="max-w-[14rem] truncate">
                    {datesChipLabel}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-[10px] opacity-50"
                  />
                </button>
              }
            >
              <div>
                <h5 className="text-sm font-bold mb-2">
                  {t("Analytics.Filters.Dates", "Dates")}
                </h5>
                <AnalyticsDatesControl
                  ref={panelOpen ? undefined : datesControlRef}
                  dateMode={dateMode}
                  onDateModeChange={setDateMode}
                  stagedDateShortcut={stagedDateShortcut}
                  dateRange={selectedValues.dateRange}
                  fitPreviewLabel={fitPreviewRangeLabel}
                  emptyActivityHint={emptyActivityHint}
                  activityWindowError={activityWindowError}
                  onRetryActivityWindow={retryActivityWindow}
                  onExactDatesCompleteChange={setExactDatesComplete}
                  onCommonDraftCompleteChange={setCommonDraftComplete}
                />
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    className="btn-primary !px-3 !py-1.5 text-sm rounded-md"
                    disabled={dateChromeDraftIncomplete}
                    title={
                      dateMode === "pick" && !exactDatesComplete
                        ? t(
                            "Analytics.Filters.ExactDatesRequired",
                            "Choose both a from and to date.",
                          )
                        : dateMode === "common" && !commonDraftComplete
                          ? t(
                              "Analytics.Filters.CommonPresetRequired",
                              "Choose a common date range.",
                            )
                          : undefined
                    }
                    onClick={handleDatesDone}
                  >
                    {t("Common.Done", "Done")}
                  </button>
                </div>
              </div>
            </Popover>

            {/* Show past data chip - sits next to Dates because it picks which version
                of the data is used, not an audience. The audience chips follow, so the
                trailing + stays the last thing on the bar. Present only while a date is
                staged (ADR-0006). */}
            {showPointInTimeChip && (
              <Popover
                openState={pastOpen}
                onOpenStateChange={setPastOpen}
                showCloseIcon={false}
                contentClassNames="w-72 bg-white p-3 border border-gray-200 shadow-lg-around"
                triggerElement={
                  <button
                    id="analytics-filter-past-data"
                    type="button"
                    className={cx(
                      "inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-bold text-gray-800 cursor-pointer",
                      pointInTimeDirty && "analytics-filter-chip--staged",
                      pointInTimeDirty &&
                        bandApplyMotion === "apply" &&
                        "analytics-filter-chip--releasing",
                      pointInTimeDirty &&
                        bandApplyMotion === "discard" &&
                        "analytics-filter-chip--collapsing",
                    )}
                  >
                    {t("Analytics.Filters.DataFrom", "Snapshot · {{date}}", {
                      date: pointInTimeChipDateLabel,
                    })}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="text-[10px] opacity-50"
                    />
                  </button>
                }
              >
                <div>
                  <div className="flex items-start gap-2 mb-2">
                    <h5 className="flex-1 text-sm font-bold">
                      {t("Analytics.Filters.ShowPastData", "Data snapshot")}
                    </h5>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      aria-label={t(
                        "Analytics.Filters.PastDataInfo.Open",
                        "What to know about data snapshots",
                      )}
                      onClick={() => setPastDataInfoOpen(true)}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </button>
                  </div>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                    value={stagedPointInTimeDate ?? ""}
                    min={pointInTimeFloor?.earliestSelectableDate ?? undefined}
                    max={latestSelectablePointInTimeDate()}
                    aria-label={t(
                      "Analytics.Filters.PastDataDateLabel",
                      "Snapshot date",
                    )}
                    onChange={(e) => handlePointInTimeDateChange(e.target.value)}
                  />
                  <p className="mt-1.5 text-xs text-gray-500">
                    {t(
                      "Analytics.Filters.PastDataDateHelper",
                      "Shows the data held at the end of the selected date (UTC). Other date filters already set above will still apply.",
                    )}
                  </p>
                  <div className="flex justify-between mt-3">
                    <button
                      type="button"
                      className="text-xs font-semibold text-gray-600 underline"
                      onClick={() => {
                        onPointInTimeDateChange(null);
                        setPastOpen(false);
                      }}
                    >
                      {t(
                        "Analytics.Filters.ShowTodaysData",
                        "Show today's data",
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn-primary !px-3 !py-1.5 text-sm rounded-md"
                      onClick={() => setPastOpen(false)}
                    >
                      {t("Common.Done", "Done")}
                    </button>
                  </div>
                </div>
              </Popover>
            )}

            {/* contents: keep #analytics-filter-audience for tours, but chips wrap as siblings of Journey/Dates. */}
            <span id="analytics-filter-audience" className="contents">
              <AudienceFacetChipBar
                selectedValues={selectedValues}
                chipClassName={chipClass}
                isFacetDirty={(type) =>
                  isAudienceFacetDirty(type, selectedValues, appliedValues)
                }
                onLocationPickerChange={onLocationPickerChange}
                onJobtitlePickerChange={onJobtitlePickerChange}
                onAppraisalLevelPickerChange={onAppraisalLevelPickerChange}
                onUserGroupPickerChange={onUserGroupPickerChange}
                onFacetPopoverOpenChange={setAudienceFacetPopoverOpen}
              />
            </span>
          </div>

          {/* __col: these controls are shorter than a chip (no chip border), so
              without it they sit a pixel above the chips and the Filters label. */}
          <div className="analytics-filter-band__col flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
            {/* Bookmark Save only while staged — otherwise the left label/popover covers saved filters. */}
            {isDirty && (
              <button
                type="button"
                className="hidden items-center gap-1.5 px-2 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 sm:inline-flex"
                onClick={() => saved.openSaveDialog(false, selectedValues)}
                aria-label={t("Common.Save", "Save")}
              >
                <FontAwesomeIcon icon={faBookmark} />
                {t("Common.Save", "Save")}
              </button>
            )}

            <span
              id="analytics-filter-pin-advanced"
              className="inline-flex flex-shrink-0 items-center gap-2"
            >
              <Tooltip
                content={
                  carryFiltersAcrossPages
                    ? t(
                        "Analytics.Filters.UnpinFiltersTooltip",
                        "Unpin - go back to each page's own filters",
                      )
                    : t(
                        "Analytics.Filters.PinFiltersTooltip",
                        "Pin filters - keep these as you change pages",
                      )
                }
                triggerElement={
                  <button
                    type="button"
                    className={cx(
                      "relative inline-flex h-8 w-8 items-center justify-center rounded-md",
                      carryFiltersAcrossPages
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-800",
                    )}
                    aria-pressed={carryFiltersAcrossPages}
                    aria-label={
                      carryFiltersAcrossPages
                        ? t(
                            "Analytics.Filters.UnpinFiltersTooltip",
                            "Unpin - go back to each page's own filters",
                          )
                        : t(
                            "Analytics.Filters.PinFiltersTooltip",
                            "Pin filters - keep these as you change pages",
                          )
                    }
                    onClick={() =>
                      onCarryFiltersAcrossPagesChange(!carryFiltersAcrossPages)
                    }
                  >
                    <FontAwesomeIcon icon={faThumbtack} className="text-sm" />
                    {carryFiltersAcrossPages && (
                      <span
                        className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-green-500"
                        aria-hidden
                      />
                    )}
                  </button>
                }
              />

              <button
                type="button"
                className="hidden items-center px-2 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 sm:inline-flex"
                onClick={() => setPanelOpen(true)}
              >
                {t("Analytics.Filters.Advanced", "Advanced")}
              </button>
            </span>
          </div>
        </div>

        {/* Staged strip: Reset here = discard unapplied edits; Advanced panel Reset = page defaults. */}
        {isDirty && (
          <div className="analytics-filter-band__staged border-t border-amber-200/80 bg-amber-50/90">
            <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-2 px-3 py-2">
              <span className="text-xs font-semibold text-gray-800">
                {t(
                  "Analytics.Filters.StaleNote",
                  "Filters changed - charts still show the last applied selection",
                )}
              </span>
              <div className="flex flex-shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary !px-3 !py-1.5 text-sm rounded-md"
                  disabled={bandApplyMotion != null || chipPopoverOpen}
                  title={
                    chipPopoverOpen
                      ? t(
                          "Analytics.Filters.FinishPopoverFirst",
                          "Finish or close the open filter first.",
                        )
                      : undefined
                  }
                  onClick={() => runBandApplyWithMotion("discard")}
                >
                  {t("Common.Reset", "Reset")}
                </button>
                <button
                  type="button"
                  className="btn-primary !px-4 !py-1.5 text-sm rounded-md font-bold"
                  disabled={
                    bandApplyMotion != null ||
                    chipPopoverOpen ||
                    dateChromeDraftIncomplete
                  }
                  title={
                    chipPopoverOpen
                      ? t(
                          "Analytics.Filters.FinishPopoverFirst",
                          "Finish or close the open filter first.",
                        )
                      : dateMode === "pick" && !exactDatesComplete
                        ? t(
                            "Analytics.Filters.ExactDatesRequired",
                            "Choose both a from and to date.",
                          )
                        : dateMode === "common" && !commonDraftComplete
                          ? t(
                              "Analytics.Filters.CommonPresetRequired",
                              "Choose a common date range.",
                            )
                          : undefined
                  }
                  onClick={() => runBandApplyWithMotion("apply")}
                >
                  {t("Analytics.Filters.Apply", "Apply")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {saved.isPartialApply &&
        !saved.partialBannerDismissed &&
        appliedSavedFilterId != null && (
          <div className="mx-1 mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-gray-800">
            <span className="flex-1">
              {t(
                "Analytics.Filters.Saved.PartialApplyBanner",
                "This page doesn't include all journeys from '{{name}}': {{journeys}}.",
                {
                  name: saved.appliedSavedItem?.name ?? "",
                  journeys: saved.partialSkippedJourneys
                    .map((label) => savedJourneyLabelForDisplay(label, t))
                    .join(", "),
                },
              )}
            </span>
            <button
              type="button"
              className="text-xs font-semibold text-gray-600 underline shrink-0"
              onClick={saved.dismissPartialBanner}
            >
              {t("Common.Dismiss", "Dismiss")}
            </button>
          </div>
        )}

      {/* Loud treatment while a past date is applied: this page is not today (ADR-0006). */}
      {showPointInTimeBanner && (
        <div className="mx-1 mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-gray-800">
          <span className="flex-1">
            {t(
              "Analytics.Filters.PastDataBanner",
              "These charts use a data snapshot for {{date}}, not today.",
              { date: pointInTimeBannerDateLabel },
            )}
          </span>
          <button
            type="button"
            className="text-xs font-semibold text-gray-700 underline shrink-0"
            onClick={() => setPastDataInfoOpen(true)}
          >
            {t("Analytics.Filters.PastDataInfo.WhatToKnow", "What to know")}
          </button>
          <button
            type="button"
            className="btn-secondary !px-3 !py-1 text-xs rounded-md shrink-0"
            onClick={() => onPointInTimeDateChange(null)}
          >
            {t("Analytics.Filters.ShowTodaysData", "Show today's data")}
          </button>
        </div>
      )}

      {panelOpen && (
        <AnalyticsAdvancedFiltersPanel
          onClose={handlePanelClose}
          carryFiltersAcrossPages={carryFiltersAcrossPages}
          onCarryFiltersAcrossPagesChange={onCarryFiltersAcrossPagesChange}
          isSkippingPageDefault={saved.isSkippingPageDefault}
          journeyFilterDefaultValueMode={journeyFilterDefaultValueMode}
          journeyFilterRestriction={journeyFilterRestriction}
          availableValues={availableValues}
          selectedValues={selectedValues}
          datesControlRef={datesControlRef}
          dateMode={dateMode}
          onDateModeChange={setDateMode}
          stagedDateShortcut={stagedDateShortcut}
          onExactDatesCompleteChange={setExactDatesComplete}
          onCommonDraftCompleteChange={setCommonDraftComplete}
          exactDatesComplete={exactDatesComplete}
          commonDraftComplete={commonDraftComplete}
          dateChromeDraftIncomplete={dateChromeDraftIncomplete}
          pointInTimeAvailability={pointInTimeAvailability}
          pointInTimeEarliestSelectableDate={
            pointInTimeFloor?.earliestSelectableDate ?? null
          }
          canUpdateApplied={saved.canUpdateApplied}
          appliedSavedItem={saved.appliedSavedItem}
          onResetFilters={() => {
            setStagedDateShortcut("FIT_TO_JOURNEYS");
            setAppliedDateShortcut("FIT_TO_JOURNEYS");
            setDateMode("fit");
            onResetFilters();
          }}
          onApply={(draft) => applyPanelDraft(draft)}
          onUpdate={(draft, onSuccess) => {
            const next = preparePanelPersistDraft(draft);
            if (next == null) return false;
            onStageFilterInputs(next);
            saved.handleUpdate(onSuccess, next);
            return true;
          }}
          onSaveAsNew={(draft) => {
            const next = preparePanelPersistDraft(draft);
            if (next == null) return false;
            onStageFilterInputs(next);
            saved.openSaveDialog(true, next);
            return true;
          }}
        />
      )}

      <AnalyticsSaveFilterDialog
        isOpen={saved.saveDialogOpen}
        onOpenChange={saved.setSaveDialogOpen}
        isPageBound={saved.isPageBoundSave}
        pageTitle={pageTitle}
        pointInTimeDateLabel={
          pointInTimeUsable
            ? formatPointInTimeForCopy(saved.pointInTimeDateForSave)
            : null
        }
        showUserGroupsPastDataWarning={saved.showUserGroupsPastDataWarning}
        initialName={saved.appliedSavedItem?.name ?? ""}
        forceCreateOnly={saved.saveDialogForceCreate}
        canUpdate={saved.canUpdateApplied}
        updateTargetName={saved.appliedSavedItem?.name ?? null}
        canManageShared={saved.canManageShared}
        nameError={saved.saveNameError}
        onClearNameError={saved.clearSaveNameError}
        onSave={saved.handleSave}
        onUpdate={saved.handleUpdate}
        isSaving={saved.isSaving}
      />

      <AnalyticsRenameFilterDialog
        isOpen={saved.renameItem != null}
        onOpenChange={(open) => {
          if (!open) {
            saved.closeRename();
          }
        }}
        currentName={saved.renameItem?.name ?? ""}
        onRename={saved.handleRenameSubmit}
        isSaving={saved.isRenaming}
        nameError={saved.renameNameError}
      />

      <AlertPopup
        isOpen={saved.deleteConfirmItem != null}
        onOpenChange={(open) => {
          if (!open) saved.setDeleteConfirmItem(null);
        }}
        title={t(
          "Analytics.Filters.Saved.DeleteTitle",
          "Delete saved filter",
        )}
        bodyText={saved.deleteConfirmBody}
        primaryButtonText={t("Common.Delete", "Delete")}
        secondaryButtonText={t("Common.Cancel", "Cancel")}
        onPrimaryButtonClick={saved.confirmDelete}
        onSecondaryButtonClick={() => saved.setDeleteConfirmItem(null)}
      />

      <AlertPopup
        isOpen={saved.updateConfirm != null}
        onOpenChange={(open) => {
          if (!open) saved.setUpdateConfirm(null);
        }}
        title={t(
          "Analytics.Filters.SaveDialog.UpdateTitle",
          "Update saved filter",
        )}
        bodyText={saved.updateConfirmBody}
        primaryButtonText={t("Common.Update", "Update")}
        secondaryButtonText={t("Common.Cancel", "Cancel")}
        onPrimaryButtonClick={saved.confirmUpdate}
        onSecondaryButtonClick={() => saved.setUpdateConfirm(null)}
      />

      <AnalyticsPastDataInfoModal
        isOpen={pastDataInfoOpen}
        onOpenChange={setPastDataInfoOpen}
      />
    </div>
  );
}

/** Readable date for Show past data copy, or null when the filter is live. */
function formatPointInTimeForCopy(
  pointInTimeDate: string | null,
): string | null {
  const display = pointInTimeDateForDisplay(pointInTimeDate);
  return display ? format(display, "d MMM yyyy") : null;
}

/** Tooltip only when CSS truncate is actually clipping the text. */
function TruncatedTextWithTooltip({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      setTruncated(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  const label = (
    <span ref={ref} className={className}>
      {text}
    </span>
  );

  if (!truncated) {
    return label;
  }

  return <Tooltip content={text} triggerElement={label} />;
}

export default AnalyticsFilters;
